// FPAdminPanel.js
// Franchise Player — admin-only tools, shown below every Franchise Player screen
// for the platform admin (same pattern as UEAdminPanel in Underdog Edge).

import React, { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "./firebase";
import { FP_ACCENT, FP_ACCENT_DIM } from "./fp-theme";

function formatSalaryDate(yyyymmdd) {
  if (!yyyymmdd || yyyymmdd.length !== 8) return "—";
  const d = new Date(`${yyyymmdd.slice(0, 4)}-${yyyymmdd.slice(4, 6)}-${yyyymmdd.slice(6, 8)}T12:00:00`);
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

function fmtTime(ms) {
  if (!ms) return "—";
  return new Date(ms).toLocaleString(undefined, { timeZone: "America/Los_Angeles", weekday: "short", hour: "numeric", minute: "2-digit" });
}

const cell = { padding: "5px 8px", whiteSpace: "nowrap" };

// Today's date in Pacific time as YYYYMMDD (matches the backend's API counter keys).
function todayPT() {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: "America/Los_Angeles", year: "numeric", month: "2-digit", day: "2-digit" })
    .formatToParts(new Date()).reduce((m, p) => ({ ...m, [p.type]: p.value }), {});
  return `${parts.year}${parts.month}${parts.day}`;
}

// Plain-English names for each Tank01 endpoint in the usage breakdown.
const API_NAMES = {
  getNFLBoxScore: "Live scores", getNFLDFS: "Salaries", getNFLPlayerList: "Players & injuries",
  getNFLADP: "ADP", getNFLTeams: "Teams & byes", getNFLGamesForWeek: "Schedule",
  getNFLProjections: "Projections", getNFLBettingOdds: "Vegas lines",
};
const apiName = (path) => API_NAMES[path] || path.replace(/^getNFL/, "");
const DAILY_LIMIT = 1000;

// The last n Pacific dates (newest first) as YYYYMMDD, counting back from today.
function lastDays(today, n) {
  const base = Date.UTC(+today.slice(0, 4), +today.slice(4, 6) - 1, +today.slice(6, 8), 12);
  return Array.from({ length: n }, (_, i) => {
    const d = new Date(base - i * 86400000);
    return `${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, "0")}${String(d.getUTCDate()).padStart(2, "0")}`;
  });
}
function dayLabel(yyyymmdd) {
  const d = new Date(Date.UTC(+yyyymmdd.slice(0, 4), +yyyymmdd.slice(4, 6) - 1, +yyyymmdd.slice(6, 8), 12));
  return d.toLocaleDateString(undefined, { timeZone: "UTC", weekday: "short", month: "short", day: "numeric" });
}

// Tank01 calls per day for the last two weeks. Tap a day to see what used them.
function UsageHistory({ usage, today }) {
  const [open, setOpen] = useState(null);
  const days = lastDays(today, 14).map((d) => ({
    date: d,
    total: usage[d] || 0,
    parts: Object.entries(usage).filter(([k]) => k.startsWith(`${d}_`))
      .map(([k, n]) => [apiName(k.slice(d.length + 1)), n]).sort((a, b) => b[1] - a[1]),
  }));
  const counted = days.filter((d) => d.total > 0);
  if (!counted.length) return null;
  const firstCounted = Object.keys(usage).filter((k) => /^\d{8}$/.test(k)).sort()[0];
  const busiest = counted.reduce((a, b) => (b.total > a.total ? b : a));
  const sum = counted.reduce((t, d) => t + d.total, 0);
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: "rgba(255,255,255,0.35)", marginBottom: 4 }}>
        TANK01 CALLS · LAST 14 DAYS
      </div>
      <p style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", marginBottom: 8 }}>
        {sum.toLocaleString()} calls · busiest {dayLabel(busiest.date)} ({busiest.total.toLocaleString()}) · daily average{" "}
        {Math.round(sum / counted.length).toLocaleString()} · plan allows {DAILY_LIMIT.toLocaleString()} a day. Tap a day for the breakdown.
      </p>
      <div role="list" style={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {days.map((d) => {
          const before = firstCounted && d.date < firstCounted;
          const over = d.total > DAILY_LIMIT;
          const isOpen = open === d.date;
          return (
            <div key={d.date} role="listitem">
              <button type="button" onClick={() => setOpen(isOpen ? null : d.date)} disabled={!d.total}
                aria-expanded={isOpen} aria-label={`${dayLabel(d.date)}: ${d.total} calls`}
                title={d.parts.map(([k, n]) => `${k} ${n}`).join(" · ")}
                style={{
                  display: "grid", gridTemplateColumns: "92px 1fr 64px", alignItems: "center", gap: 10, width: "100%",
                  background: isOpen ? "rgba(255,255,255,0.05)" : "transparent", border: "none", borderRadius: 6,
                  padding: "4px 6px", cursor: d.total ? "pointer" : "default", color: "inherit", font: "inherit", textAlign: "left",
                }}>
                <span style={{ fontSize: 12, color: d.date === today ? "white" : "rgba(255,255,255,0.55)", fontWeight: d.date === today ? 700 : 400 }}>
                  {d.date === today ? "Today" : dayLabel(d.date)}
                </span>
                <span style={{ position: "relative", height: 10, background: "rgba(255,255,255,0.06)", borderRadius: 4 }}>
                  <span style={{
                    position: "absolute", left: 0, top: 0, bottom: 0, borderRadius: 4,
                    width: `${Math.min(100, (d.total / DAILY_LIMIT) * 100)}%`, background: FP_ACCENT,
                  }} />
                </span>
                <span style={{ fontSize: 12, textAlign: "right", fontVariantNumeric: "tabular-nums", color: over ? "#FBBF24" : "rgba(255,255,255,0.8)" }}>
                  {before ? "—" : d.total.toLocaleString()}{over ? " ⚠" : ""}
                </span>
              </button>
              {isOpen && d.parts.length > 0 && (
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", padding: "2px 6px 6px 108px" }}>
                  {d.parts.map(([k, n]) => `${k} ${n}`).join(" · ")}
                  {over && <span style={{ color: "#FBBF24" }}> · over the daily {DAILY_LIMIT.toLocaleString()} (1¢ per extra call)</span>}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {firstCounted && firstCounted > days[days.length - 1].date && (
        <p style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", marginTop: 6 }}>Counting started {dayLabel(firstCounted)}.</p>
      )}
    </div>
  );
}

const STYLE_LABELS = { best: "Best projected", stack: "QB stack", rb: "RB heavy", wr: "WR heavy", balanced: "Balanced" };

// Results of the projection check: one block per finished week.
function ProjectionCheck({ result }) {
  const [openStyle, setOpenStyle] = useState(null);
  if (!result) return null;
  if (!result.weeks?.length) {
    return <p style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", marginTop: 10 }}>No finished weeks with stored projections and box scores yet.</p>;
  }
  const cell = { padding: "4px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: "rgba(255,255,255,0.35)", marginBottom: 6 }}>
        PROJECTION CHECK · RAN {fmtTime(result.ranAt)}
      </div>
      {result.weeks.map((w) => {
        const a = w.accuracy;
        const bias = a.avgActual - a.avgProjected;
        const late = !!(w.projectionsUpdatedAt && w.firstKickoff && w.projectionsUpdatedAt > w.firstKickoff);
        return (
          <div key={w.week} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: 12, marginBottom: 10 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Week {w.week}</div>
            <p style={{ fontSize: 12, color: "rgba(255,255,255,0.6)", marginBottom: 8, lineHeight: 1.6 }}>
              <b style={{ color: "white" }}>Projections:</b> {a.players} players projected 5+ pts. Off by {a.avgMiss} pts per player on average.
              Averaged {a.avgProjected} projected vs {a.avgActual} actual ({Math.abs(bias) < 0.5 ? "about right overall" : bias > 0 ? `ran ${bias.toFixed(1)} low` : `ran ${(-bias).toFixed(1)} high`}).
              Correlation {a.correlation} (1 = perfect; 0.5–0.6 is typical for fantasy projections).
            </p>
            <div style={{ overflowX: "auto" }}>
              <table style={{ borderCollapse: "collapse", fontSize: 12, width: "100%" }}>
                <thead>
                  <tr style={{ color: "rgba(255,255,255,0.4)" }}>
                    <th style={{ ...cell, textAlign: "left" }}>Auto-lineup</th><th style={cell}>Projected</th><th style={cell}>Actual</th><th style={cell}>Beat</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(w.styles).map(([k, v]) => (
                    <React.Fragment key={k}>
                      <tr style={{ borderTop: "1px solid rgba(255,255,255,0.06)", cursor: "pointer" }} onClick={() => setOpenStyle(openStyle === `${w.week}${k}` ? null : `${w.week}${k}`)}>
                        <td style={{ ...cell, textAlign: "left" }}>{STYLE_LABELS[k] || k} <span style={{ color: "rgba(255,255,255,0.35)" }}>▾</span></td>
                        <td style={cell}>{v.projected}</td>
                        <td style={{ ...cell, fontWeight: 700 }}>{v.actual}</td>
                        <td style={cell}>{v.percentile}% of field</td>
                      </tr>
                      {openStyle === `${w.week}${k}` && (
                        <tr><td colSpan={4} style={{ ...cell, textAlign: "left", whiteSpace: "normal", color: "rgba(255,255,255,0.55)" }}>
                          {v.players.map((p) => `${p.name} (${p.pos === "DEF" ? "D/ST" : p.pos}, $${p.fd.toLocaleString()}): ${p.proj ?? "—"} proj → ${p.actual}`).join(" · ")}
                        </td></tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            <p style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", marginTop: 8, lineHeight: 1.6 }}>
              <b style={{ color: "white" }}>Field</b> ({w.field.lineups.toLocaleString()} sensible full-budget lineups, picks leaning toward better projections):
              median {w.field.median} · top 10% {w.field.top10} · top 1% {w.field.top1} · best {w.field.best}.
              Best possible in hindsight: {w.hindsight}.
              {w.realLineups?.length > 0 && <> Your leagues' real lineups (without captain): {w.realLineups.length}, best {w.realLineups[0]}, median {w.realLineups[Math.floor(w.realLineups.length / 2)]}.</>}
            </p>
            {late && (
              <p style={{ fontSize: 11, color: "#FBBF24", marginTop: 4 }}>
                Projections were last refreshed {fmtTime(w.projectionsUpdatedAt)}, after some of this week's games had started, so a few may include late news.
              </p>
            )}
          </div>
        );
      })}
      <p style={{ fontSize: 11, color: "rgba(255,255,255,0.35)" }}>
        Each lineup assumes a franchise player in a WR spot and $50,000 for the other 8 (his points are the same for everyone, so they're left out).
        Tap a style to see its players.
      </p>
    </div>
  );
}

function Tile({ label, value, tone }) {
  return (
    <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "10px 14px" }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: "rgba(255,255,255,0.35)", marginBottom: 2 }}>{label}</div>
      <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, letterSpacing: 1, color: tone || "white" }}>{value}</div>
    </div>
  );
}

function formatTimestamp(ts) {
  if (!ts?.toDate) return "—";
  return ts.toDate().toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function FPAdminPanel() {
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const [week, setWeek] = useState(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, "fp_system", "playerSync"), (snap) => {
      setStatus(snap.exists() ? snap.data() : null);
    });
    return unsub;
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, "fp_system", "weekPricing"), (snap) => {
      setWeek(snap.exists() ? snap.data() : null);
    });
    return unsub;
  }, []);

  const [timing, setTiming] = useState({});
  useEffect(() => {
    const unsub = onSnapshot(doc(db, "fp_system", "fdTiming"), (snap) => {
      setTiming((snap.exists() && snap.data().weeks) || {});
    });
    return unsub;
  }, []);
  const timingRows = Object.values(timing).sort((a, b) => b.week - a.week);

  // Stats: live scoring, projections/odds, matchup table, API usage
  const [live, setLive] = useState(null);
  const [info, setInfo] = useState(null);
  const [dvp, setDvp] = useState(null);
  const [usage, setUsage] = useState({});
  const [loadingPast, setLoadingPast] = useState(false);
  const [refreshingInfo, setRefreshingInfo] = useState(false);
  const [checkResult, setCheckResult] = useState(null);
  const [recaps, setRecaps] = useState({});
  const [recapBusy, setRecapBusy] = useState(null);
  const [checking2, setChecking2] = useState(false);
  useEffect(() => {
    const subs = [
      onSnapshot(doc(db, "fp_system", "liveScoring"), (s) => setLive(s.exists() ? s.data() : null), () => {}),
      onSnapshot(doc(db, "fp_system", "weekInfo"), (s) => setInfo(s.exists() ? s.data() : null), () => {}),
      onSnapshot(doc(db, "fp_system", "dvp"), (s) => setDvp(s.exists() ? s.data() : null), () => {}),
      onSnapshot(doc(db, "fp_system", "apiUsage"), (s) => setUsage(s.exists() ? s.data() : {}), () => {}),
      onSnapshot(doc(db, "fp_system", "projectionCheck"), (s) => setCheckResult(s.exists() ? s.data() : null), () => {}),
      onSnapshot(doc(db, "fp_system", "recaps"), (s) => setRecaps(s.exists() ? s.data() : {}), () => {}),
    ];
    return () => subs.forEach((u) => u());
  }, []);
  const today = todayPT();
  const callsToday = usage[today] || 0;
  const callsByPath = Object.entries(usage)
    .filter(([k]) => k.startsWith(`${today}_`))
    .map(([k, n]) => [apiName(k.slice(today.length + 1)), n])
    .sort((a, b) => b[1] - a[1]);
  const gamesStored = dvp?.games ? Math.round(Object.values(dvp.games).reduce((s, n) => s + n, 0) / 2) : null;

  const loadPastStats = async () => {
    setLoadingPast(true);
    setMessage("");
    setIsError(false);
    try {
      const r = (await httpsCallable(getFunctions(), "fpLoadPastStats", { timeout: 545000 })({})).data;
      setMessage(`Loaded ${r.loaded} of ${r.finalGames} finished games` +
        (r.weeks?.length ? ` (weeks ${r.weeks.join(", ")})` : "") +
        `. Game logs, matchup ranks and scores updated.` +
        (r.errors?.length ? ` Errors: ${r.errors.join("; ")}` : "") +
        (r.loaded < r.finalGames ? " Run it again to pick up the rest." : ""));
    } catch (e) {
      setIsError(true);
      setMessage((e.message || "Load failed.") + " Anything already loaded is kept; run it again to continue.");
    }
    setLoadingPast(false);
  };

  const runProjectionCheck = async () => {
    setChecking2(true);
    setMessage("");
    setIsError(false);
    try {
      const r = (await httpsCallable(getFunctions(), "fpProjectionCheck", { timeout: 300000 })({})).data;
      setCheckResult(r);
      setMessage(r.weeks.length ? `Checked ${r.weeks.length} finished week${r.weeks.length > 1 ? "s" : ""}: ${r.weeks.map((w) => w.week).join(", ")}.` : "No finished weeks to check yet.");
    } catch (e) {
      setIsError(true);
      setMessage(e.message || "Check failed.");
    }
    setChecking2(false);
  };

  const sendRecap = async (preview) => {
    if (!preview && !window.confirm("Send last week's recap email to every manager now? It only goes out once per week.")) return;
    setRecapBusy(preview ? "preview" : "send");
    setMessage("");
    setIsError(false);
    try {
      const r = (await httpsCallable(getFunctions(), "fpSendRecapNow", { timeout: 300000 })({ preview })).data;
      setMessage(r.skipped ? r.skipped
        : preview ? `Preview of the week ${r.week} recap sent to you (${r.sent} email${r.sent === 1 ? "" : "s"}, one per league).`
        : `Week ${r.week} recap sent: ${r.sent} emails across ${r.leagues} league${r.leagues === 1 ? "" : "s"}.` + (r.errors?.length ? ` Errors: ${r.errors.join("; ")}` : ""));
    } catch (e) {
      setIsError(true);
      setMessage(e.message || "Recap failed.");
    }
    setRecapBusy(null);
  };

  const refreshInfo = async () => {
    setRefreshingInfo(true);
    setMessage("");
    setIsError(false);
    try {
      const r = (await httpsCallable(getFunctions(), "fpRefreshWeekInfoNow")({})).data;
      setMessage(`Week ${r.week}: ` + [
        r.projections ? `projections for ${r.projections.players} players` : `projections failed (${r.projectionsError})`,
        r.odds ? `Vegas lines for ${r.odds.teams} teams, ${r.odds.props} TD props` : `odds failed (${r.oddsError})`,
      ].join(" · "));
      setIsError(!r.projections || !r.odds);
    } catch (e) {
      setIsError(true);
      setMessage(e.message || "Refresh failed.");
    }
    setRefreshingInfo(false);
  };

  const checkSalaries = async () => {
    setChecking(true);
    setMessage("");
    setIsError(false);
    try {
      const r = (await httpsCallable(getFunctions(), "fpCheckSalariesNow")({})).data;
      setMessage(r.skipped
        ? r.skipped
        : `Week ${r.week}: FanDuel has ${r.fdTeams} of ${r.teamsPlaying} teams. ${r.released ? `Released${r.estimated ? ` with ${r.estimated} estimated prices` : ""}${r.releasedNow ? `, ${r.emailed ?? 0} emails sent` : ""}.` : r.missingTeams.length ? `Missing ${r.missingTeams.join(", ")}. Goes live Wednesday 10am.` : "FanDuel complete. Goes live Wednesday 10am."}`);
    } catch (e) {
      setIsError(true);
      setMessage(e.message || "Check failed.");
    }
    setChecking(false);
  };

  const syncNow = async () => {
    setSyncing(true);
    setMessage("");
    setIsError(false);
    try {
      const result = await httpsCallable(getFunctions(), "fpSyncPlayersNow")({});
      const r = result.data;
      setMessage(`Synced ${r.players} players for ${formatSalaryDate(r.salaryDate)}: ${r.fd} FanDuel prices, ${r.adp} ADP rankings, ${r.injured} injured, ${r.byeTeams} team byes.` + (r.warnings?.length ? ` Warnings: ${r.warnings.join("; ")}` : ""));
    } catch (e) {
      setIsError(true);
      setMessage(e.message || "Sync failed.");
    }
    setSyncing(false);
  };

  return (
    <div className="card" style={{ marginTop: 32, padding: 20, borderColor: "rgba(20,184,166,0.3)" }}>
      <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 20, letterSpacing: 2, color: FP_ACCENT, marginBottom: 4 }}>
        ADMIN · PLAYER DATA
      </div>
      <p style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", marginBottom: 14 }}>
        Players, FanDuel salaries, ADP, injuries and bye weeks sync from Tank01 automatically every day at 11am Pacific. Game-day injury updates run separately before kickoffs.
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 14 }}>
        {[
          ["LAST SYNC", status ? formatTimestamp(status.lastRunAt) : "Never"],
          ["STATUS", status ? (status.ok ? "OK" : "Failed") : "—"],
          ["SALARIES FOR", status ? formatSalaryDate(status.salaryDate) : "—"],
          ["PLAYERS", status?.players != null ? String(status.players) : "—"],
          ["FANDUEL PRICES", status ? String(status.fd ?? "—") : "—"],
          ["ADP RANKED", status ? String(status.adp ?? "—") : "—"],
          ["INJURED", status ? String(status.injured ?? "—") : "—"],
        ].map(([label, value]) => (
          <div key={label} style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "10px 14px" }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: "rgba(255,255,255,0.35)", marginBottom: 2 }}>{label}</div>
            <div style={{
              fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, letterSpacing: 1,
              color: label === "STATUS" && status && !status.ok ? "var(--red)" : "white",
            }}>
              {value}
            </div>
          </div>
        ))}
      </div>

      {status?.ok && status.warnings?.length > 0 && (
        <div style={{ fontSize: 12, color: "#FBBF24", marginBottom: 12, lineHeight: 1.6 }}>
          Notes from the last sync:
          {status.warnings.map((w, i) => <div key={i}>• {w}</div>)}
        </div>
      )}

      {status && !status.ok && status.error && (
        <p className="error-text" style={{ marginBottom: 12 }}>
          Last error: {status.error}
          {status.players != null && <span style={{ color: "rgba(255,255,255,0.4)" }}> (numbers above are from the last good sync)</span>}
        </p>
      )}

      <button onClick={syncNow} disabled={syncing} className="btn btn-fp btn-sm">
        {syncing ? "Syncing..." : "🔄 Sync players & salaries now"}
      </button>

      <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 20, letterSpacing: 2, color: FP_ACCENT, margin: "22px 0 4px" }}>
        ADMIN · WEEKLY SALARIES
      </div>
      <p style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", marginBottom: 14 }}>
        Salaries go live every Wednesday at 10am Pacific. From Tuesday 6am, FanDuel is checked every 15 minutes until every team playing
        is priced. Any team still missing at 10am gets DraftKings-based estimates. Prices are frozen once seen. If FanDuel wasn't complete
        at release, it keeps checking every 30 minutes through Thursday to record when it would have been.
      </p>
      {week?.error && week.errorAt > (week.lastPullAt || 0) && (
        <p className="error-text" style={{ marginBottom: 12 }}>Last error: {week.error}</p>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 14 }}>
        {[
          ["WEEK", week?.week ? String(week.week) : "—"],
          ["STATUS", week?.week ? (week.released ? "Released" : "Waiting") : "—"],
          ["TEAMS PRICED", week?.week ? `${week.fdTeams ?? 0} / ${week.teamsPlaying ?? "?"}` : "—"],
          ["PLAYERS PRICED", week?.priced != null ? String(week.priced) : "—"],
          ["ESTIMATED", week?.estimated != null ? String(week.estimated) : "—"],
          ["LAST CHECK", week?.lastPullAt ? new Date(week.lastPullAt).toLocaleString(undefined, { weekday: "short", hour: "numeric", minute: "2-digit" }) : "—"],
        ].map(([label, value]) => (
          <div key={label} style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "10px 14px" }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: "rgba(255,255,255,0.35)", marginBottom: 2 }}>{label}</div>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, letterSpacing: 1, color: label === "STATUS" && week?.released ? FP_ACCENT : "white" }}>{value}</div>
          </div>
        ))}
      </div>
      {week && !week.released && week.missingTeams?.length > 0 && (
        <p style={{ fontSize: 12, color: "#FBBF24", marginBottom: 12 }}>FanDuel missing: {week.missingTeams.join(", ")}</p>
      )}
      {week?.listSeenAt && Object.keys(week.listSeenAt).length > 0 && (
        <p style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", marginBottom: 12 }}>
          Lists first seen: {Object.entries(week.listSeenAt).sort().map(([d, t]) =>
            `${formatSalaryDate(d)} ${new Date(t).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`).join(" · ")}
        </p>
      )}
      {week && !week.released && week.lastPullNote && (
        <p style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", marginBottom: 12 }}>Last pull: {week.lastPullNote}</p>
      )}
      {week?.reason && <p style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", marginBottom: 12 }}>{week.reason}</p>}
      <button onClick={checkSalaries} disabled={checking} className="btn btn-fp btn-sm">
        {checking ? "Checking..." : "💰 Check this week's salaries now"}
      </button>

      {timingRows.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: "rgba(255,255,255,0.35)", marginBottom: 6 }}>
            WHEN FANDUEL LANDS (Pacific time, to within 15 minutes)
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, color: "rgba(255,255,255,0.7)" }}>
              <thead>
                <tr style={{ color: "rgba(255,255,255,0.4)", textAlign: "left" }}>
                  {["Week", "Tue list posted", "Wed list posted", "FanDuel complete", "At 10am release"].map((h) => (
                    <th key={h} style={{ padding: "4px 8px", fontWeight: 600, whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {timingRows.map((t) => (
                  <tr key={t.week} style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                    <td style={cell}>{t.week}</td>
                    <td style={cell}>{fmtTime(t.tueListAt)}</td>
                    <td style={cell}>{fmtTime(t.wedListAt)}</td>
                    <td style={{ ...cell, color: t.fdCompleteAt && (!t.releasedAt || t.fdCompleteAt <= t.releasedAt) ? FP_ACCENT : "#FBBF24" }}>
                      {t.fdCompleteAt ? fmtTime(t.fdCompleteAt) : t.releasedAt ? "not yet" : "—"}
                    </td>
                    <td style={cell}>
                      {t.releasedAt ? (t.estimated ? `${t.fdTeamsAtRelease}/${t.teamsPlaying} teams · ${t.estimated} est.` : "Complete") : "Not released"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 20, letterSpacing: 2, color: FP_ACCENT, margin: "22px 0 4px" }}>
        ADMIN · STATS &amp; SCORING
      </div>
      <p style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", marginBottom: 14 }}>
        Box scores are pulled every 5 minutes while games are on and lineups are scored automatically. Finished games feed the game logs
        and matchup ranks. Last week's games are re-pulled Tuesday 5am for stat corrections. Projections and Vegas lines refresh
        Wed 10:15, Thu 9am &amp; 4pm, Fri 9am, Sat 9am, Sun 5:45am &amp; 9:15am and Mon noon. Tank01 Pro plan: 1,000 calls a day, then 1¢ per extra call.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 14 }}>
        <Tile label="GAMES STORED" value={gamesStored != null ? String(gamesStored) : "None"} tone={gamesStored ? "white" : "#FBBF24"} />
        <Tile label="LIVE SCORING" value={live?.lastRunAt ? fmtTime(live.lastRunAt) : "Not yet"} />
        <Tile label="GAMES IN LAST RUN" value={live?.lastRunAt ? `${live.live ?? 0} live · ${live.finals ?? 0} final` : "—"} />
        <Tile label={`WEEK ${info?.week ?? "—"} PROJECTIONS`} value={info?.projections ? `${info.projections.players} players` : info?.projectionsError ? "Failed" : "—"}
          tone={info?.projectionsError ? "var(--red)" : "white"} />
        <Tile label="VEGAS LINES" value={info?.odds ? `${info.odds.teams} teams` : info?.oddsError ? "Failed" : "—"} tone={info?.oddsError ? "var(--red)" : "white"} />
        <Tile label="LINES UPDATED" value={info?.lastRunAt ? fmtTime(info.lastRunAt) : "—"} />
        <Tile label="API CALLS TODAY" value={`${callsToday} / 1,000`} tone={callsToday > 850 ? "var(--red)" : callsToday > 600 ? "#FBBF24" : "white"} />
      </div>
      {callsByPath.length > 0 && (
        <p style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", marginBottom: 12 }}>
          Calls today: {callsByPath.map(([k, n]) => `${k} ${n}`).join(" · ")}
        </p>
      )}
      <UsageHistory usage={usage} today={today} />
      {(info?.projectionsError || info?.oddsError) && (
        <p className="error-text" style={{ marginBottom: 12 }}>
          {info.projectionsError && <>Projections: {info.projectionsError}. </>}
          {info.oddsError && <>Odds: {info.oddsError}</>}
        </p>
      )}
      {live?.errors?.length > 0 && (
        <p className="error-text" style={{ marginBottom: 12 }}>Last live-scoring errors: {live.errors.slice(0, 3).join("; ")}</p>
      )}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button onClick={loadPastStats} disabled={loadingPast} className="btn btn-fp btn-sm">
          {loadingPast ? "Loading box scores (a few minutes)..." : "📊 Load past box scores"}
        </button>
        <button onClick={refreshInfo} disabled={refreshingInfo} className="btn btn-ghost btn-sm">
          {refreshingInfo ? "Refreshing..." : "🎲 Refresh projections & Vegas lines now"}
        </button>
        <button onClick={runProjectionCheck} disabled={checking2} className="btn btn-ghost btn-sm">
          {checking2 ? "Checking (up to a minute)..." : "📐 Check projections & auto-lineups"}
        </button>
      </div>
      <p style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", marginTop: 8 }}>
        Load past box scores is a one-time catch-up for games played before stats were switched on (about one API call per game). It skips games already stored, so it's safe to run again.
        The projection check replays finished weeks from stored data (no API calls) and changes nothing.
      </p>
      <ProjectionCheck result={checkResult} />

      <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 20, letterSpacing: 2, color: FP_ACCENT, margin: "22px 0 4px" }}>
        ADMIN · WEEKLY RECAP
      </div>
      <p style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", marginBottom: 10 }}>
        Sends automatically Tuesday 9am Pacific once every game of the week is final (checks hourly until 1pm), after the 5am stat corrections.
        Each manager gets one email per league: the winner, their score, their perfect lineup, the league's perfect benched-captain lineup and the standings.
      </p>
      {Object.keys(recaps).filter((k) => /^\d+$/.test(k)).length > 0 && (
        <p style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", marginBottom: 10 }}>
          Sent: {Object.entries(recaps).filter(([k]) => /^\d+$/.test(k)).sort((a, b) => b[0] - a[0]).slice(0, 4)
            .map(([w, r]) => `week ${w} on ${fmtTime(r.sentAt)} (${r.sent} emails)`).join(" · ")}
        </p>
      )}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button onClick={() => sendRecap(true)} disabled={!!recapBusy} className="btn btn-ghost btn-sm">
          {recapBusy === "preview" ? "Sending preview..." : "👀 Preview last week's recap (to me)"}
        </button>
        <button onClick={() => sendRecap(false)} disabled={!!recapBusy} className="btn btn-ghost btn-sm">
          {recapBusy === "send" ? "Sending..." : "📧 Send last week's recap now"}
        </button>
      </div>

      {message && (
        <p style={{
          fontSize: 13, marginTop: 10, padding: "8px 12px", borderRadius: 8,
          color: isError ? "var(--red)" : FP_ACCENT,
          background: isError ? "rgba(229,57,53,0.1)" : FP_ACCENT_DIM,
        }}>
          {message}
        </p>
      )}
    </div>
  );
}
